import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Search, CheckSquare, AlertTriangle, Users,
  ChevronDown, X, Check, MessageSquare, Timer, LayoutGrid, List,
  Repeat, Star, Eye, Trash2, Send,
  ChevronRight, Circle,
} from 'lucide-react';
import api from '../utils/api';
import { fmtDate, deadlineClass } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import BulkActionBar from '../components/BulkActionBar';
import toast from 'react-hot-toast';

// ── Constants ──────────────────────────────────────────────────────────────────
const PRIORITIES = ['High', 'Medium', 'Low'];
const STATUSES   = ['To Do', 'In Progress', 'Waiting', 'Review Pending', 'Done', 'Cancelled'];
const TASK_TYPES = ['general', 'compliance', 'client', 'internal', 'followup'];
const RECURRING  = ['none', 'daily', 'weekly', 'monthly', 'quarterly'];
const MODULES    = ['ITR', 'TDS', 'GST', 'Audit', 'ROC', 'ESI-PF', 'Accounts'];

const KANBAN_COLS = [
  { status: 'To Do',          label: 'To Do',      color: 'border-gray-400',   bg: 'bg-gray-50',    dot: 'bg-gray-400',   accent: 'border-l-gray-300'   },
  { status: 'In Progress',    label: 'In Progress', color: 'border-blue-400',  bg: 'bg-blue-50',    dot: 'bg-blue-500',   accent: 'border-l-blue-400'   },
  { status: 'Waiting',        label: 'Waiting',    color: 'border-amber-400',  bg: 'bg-amber-50',   dot: 'bg-amber-400',  accent: 'border-l-amber-400'  },
  { status: 'Review Pending', label: 'In Review',  color: 'border-purple-400', bg: 'bg-purple-50',  dot: 'bg-purple-500', accent: 'border-l-purple-400' },
  { status: 'Done',           label: 'Done',       color: 'border-green-400',  bg: 'bg-green-50',   dot: 'bg-green-500',  accent: 'border-l-green-400'  },
];

const PRIORITY_COLOR = { High: 'text-red-600 bg-red-50', Medium: 'text-amber-600 bg-amber-50', Low: 'text-green-600 bg-green-50' };
const STATUS_COLOR   = {
  'To Do': 'bg-gray-100 text-gray-600', 'In Progress': 'bg-blue-100 text-blue-700',
  'Review Pending': 'bg-amber-100 text-amber-700', 'Done': 'bg-green-100 text-green-700',
  'Cancelled': 'bg-red-100 text-red-500',
};
const TYPE_COLOR = {
  general: 'bg-gray-100 text-gray-600', compliance: 'bg-purple-100 text-purple-700',
  client: 'bg-blue-100 text-blue-700', internal: 'bg-indigo-100 text-indigo-700',
  followup: 'bg-orange-100 text-orange-700',
};

const emptyForm = () => ({ title: '', description: '', client_id: '', assigned_to: '',
  priority: 'Medium', status: 'To Do', due_date: '', task_type: 'general',
  estimated_hours: '', recurring: 'none', recurring_end_date: '', waiting_for: '',
  review_required: false, reviewer_id: '', compliance_module: '', parent_task_id: '' });

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color = 'text-[#1e3a5f]', sub }) {
  return (
    <div className="card flex items-center gap-3 py-3">
      <div className={`p-2 rounded-xl bg-gray-50 ${color}`}><Icon size={18} /></div>
      <div>
        <div className={`text-2xl font-bold ${color}`}>{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
        {sub && <div className="text-xs text-gray-400">{sub}</div>}
      </div>
    </div>
  );
}

// ── Task form modal ────────────────────────────────────────────────────────────
function TaskModal({ editing, staff, onSave, onClose }) {
  const [form, setForm] = useState(editing || emptyForm());
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const titleRef = useRef();
  useEffect(() => { titleRef.current?.focus(); }, []);

  const handleSave = async e => {
    e.preventDefault();
    if (!form.title?.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form,
        client_id: form.client_id || null, assigned_to: form.assigned_to || null,
        reviewer_id: form.reviewer_id || null, parent_task_id: form.parent_task_id || null,
        estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
        review_required: form.review_required ? 1 : 0,
      };
      if (editing?.id) await api.put(`/tasks/${editing.id}`, payload);
      else await api.post('/tasks', payload);
      toast.success(editing?.id ? 'Task updated' : 'Task created');
      onSave();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-[#1e3a5f] rounded-t-2xl">
          <h3 className="font-semibold text-white">{editing?.id ? 'Edit Task' : 'New Task'}</h3>
          <button onClick={onClose} className="text-blue-200 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input ref={titleRef} required value={form.title} onChange={e => f('title', e.target.value)}
              className="input-field" placeholder="What needs to be done?" />
          </div>
          {/* Type + Priority + Status */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select value={form.task_type} onChange={e => f('task_type', e.target.value)} className="input-field capitalize">
                {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <select value={form.priority} onChange={e => f('priority', e.target.value)} className="input-field">
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.status} onChange={e => f('status', e.target.value)} className="input-field">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {/* Client + Assignee */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
              <ClientSearchDropdown value={form.client_id || ''} initialLabel={form.client_name || ''}
                onChange={(id, name) => setForm(p => ({ ...p, client_id: id, client_name: name }))}
                placeholder="General (no client)…" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assign To</label>
              <select value={form.assigned_to || ''} onChange={e => f('assigned_to', e.target.value)} className="input-field">
                <option value="">Unassigned</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          {/* Due date + Est. hours */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
              <input type="date" value={form.due_date || ''} onChange={e => f('due_date', e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Hours</label>
              <input type="number" step="0.5" min="0" value={form.estimated_hours || ''} onChange={e => f('estimated_hours', e.target.value)}
                className="input-field" placeholder="e.g. 2.5" />
            </div>
          </div>
          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => f('description', e.target.value)}
              className="input-field resize-none" rows={2} placeholder="Additional details…" />
          </div>
          {/* Recurring */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Recurring</label>
              <select value={form.recurring || 'none'} onChange={e => f('recurring', e.target.value)} className="input-field capitalize">
                {RECURRING.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {form.recurring && form.recurring !== 'none' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Recurring End Date</label>
                <input type="date" value={form.recurring_end_date || ''} onChange={e => f('recurring_end_date', e.target.value)} className="input-field" />
              </div>
            )}
          </div>
          {/* Compliance module */}
          {form.task_type === 'compliance' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Compliance Module</label>
              <select value={form.compliance_module || ''} onChange={e => f('compliance_module', e.target.value)} className="input-field">
                <option value="">Select module…</option>
                {MODULES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          )}
          {/* Waiting for */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Waiting For</label>
            <input value={form.waiting_for || ''} onChange={e => f('waiting_for', e.target.value)}
              className="input-field" placeholder="Who or what are we waiting on?" />
          </div>
          {/* Review required */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.review_required} onChange={e => f('review_required', e.target.checked)}
                className="w-4 h-4 rounded" />
              <span className="text-xs font-medium text-gray-700">Review required before closing</span>
            </label>
          </div>
          {form.review_required && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reviewer</label>
              <select value={form.reviewer_id || ''} onChange={e => f('reviewer_id', e.target.value)} className="input-field">
                <option value="">Select reviewer…</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
        </form>
        <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary py-2.5">
            {saving ? 'Saving…' : editing?.id ? 'Update Task' : 'Create Task'}
          </button>
          <button onClick={onClose} className="flex-1 btn-secondary py-2.5">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Time log modal ─────────────────────────────────────────────────────────────
function TimeLogModal({ taskId, taskTitle, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ hours_spent: '', work_date: today, description: '', is_billable: true });
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get(`/tasks/${taskId}/time-logs`).then(r => setLogs(r.data)).catch(() => {});
  }, [taskId]);

  const save = async e => {
    e.preventDefault();
    if (!form.hours_spent || parseFloat(form.hours_spent) <= 0) { toast.error('Enter valid hours'); return; }
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/time-logs`, form);
      toast.success('Time logged');
      onSaved();
      const r = await api.get(`/tasks/${taskId}/time-logs`);
      setLogs(r.data);
      setForm({ hours_spent: '', work_date: today, description: '', is_billable: true });
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const deleteLog = async lid => {
    await api.delete(`/tasks/${taskId}/time-logs/${lid}`);
    setLogs(l => l.filter(x => x.id !== lid));
    onSaved();
  };

  const total = logs.reduce((s, l) => s + l.hours_spent, 0);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b bg-[#1e3a5f] rounded-t-2xl">
          <Timer size={16} className="text-[#f0a500]" />
          <div>
            <div className="text-white font-semibold text-sm">Log Time</div>
            <div className="text-blue-200 text-xs truncate max-w-xs">{taskTitle}</div>
          </div>
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hours *</label>
              <input type="number" step="0.25" min="0.25" value={form.hours_spent}
                onChange={e => f('hours_spent', e.target.value)} className="input-field" placeholder="e.g. 1.5" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input type="date" value={form.work_date} onChange={e => f('work_date', e.target.value)} className="input-field" required />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Work Description</label>
            <input value={form.description} onChange={e => f('description', e.target.value)} className="input-field" placeholder="What was done?" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_billable} onChange={e => f('is_billable', e.target.checked)} className="w-4 h-4 rounded" />
            <span className="text-xs text-gray-600">Billable hours</span>
          </label>
          <button type="submit" disabled={saving} className="w-full btn-primary py-2">{saving ? 'Logging…' : 'Log Time'}</button>
        </form>
        {logs.length > 0 && (
          <div className="border-t px-5 pb-4">
            <div className="flex items-center justify-between py-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">History</span>
              <span className="text-xs font-bold text-[#1e3a5f]">Total: {total.toFixed(1)}h</span>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {logs.map(l => (
                <div key={l.id} className="flex items-center gap-2 text-xs py-1 border-b border-gray-50">
                  <span className="font-semibold text-[#1e3a5f] w-10">{l.hours_spent}h</span>
                  <span className="text-gray-500 w-20">{fmtDate(l.work_date)}</span>
                  <span className="text-gray-600 flex-1 truncate">{l.description || l.user_name}</span>
                  {!l.is_billable && <span className="text-gray-400">non-bill</span>}
                  <button onClick={() => deleteLog(l.id)} className="text-gray-300 hover:text-red-400 ml-1"><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Comments panel ─────────────────────────────────────────────────────────────
function CommentsPanel({ taskId, taskTitle, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const { user } = useAuth();
  const endRef = useRef();

  const load = useCallback(() => api.get(`/tasks/${taskId}/comments`).then(r => {
    setComments(r.data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }), [taskId]);

  useEffect(() => { load(); }, [load]);

  const send = async e => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/tasks/${taskId}/comments`, { comment: text }); setText(''); load(); }
    catch { toast.error('Failed'); }
    finally { setSending(false); }
  };

  const saveEdit = async cid => {
    await api.put(`/tasks/${taskId}/comments/${cid}`, { comment: editText });
    setEditing(null); load();
  };

  const del = async cid => {
    await api.delete(`/tasks/${taskId}/comments/${cid}`);
    load();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end justify-center sm:items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b bg-[#1e3a5f] rounded-t-2xl">
          <MessageSquare size={16} className="text-[#f0a500]" />
          <div>
            <div className="text-white font-semibold text-sm">Comments</div>
            <div className="text-blue-200 text-xs truncate max-w-xs">{taskTitle}</div>
          </div>
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {comments.length === 0 && <p className="text-center text-gray-400 text-sm py-6">No comments yet. Start the conversation.</p>}
          {comments.map(c => (
            <div key={c.id} className="flex gap-2">
              <div className="w-7 h-7 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white text-xs flex-shrink-0 mt-0.5">
                {c.user_name?.[0]}
              </div>
              <div className="flex-1">
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-700">{c.user_name}</span>
                    <span className="text-xs text-gray-400">{fmtDate(c.created_at?.split('T')[0])}</span>
                    {c.is_edited ? <span className="text-xs text-gray-400 italic">(edited)</span> : null}
                  </div>
                  {editing === c.id ? (
                    <div className="flex gap-1">
                      <input value={editText} onChange={e => setEditText(e.target.value)} className="input-field text-xs py-1 flex-1"
                        onKeyDown={e => { if (e.key === 'Enter') saveEdit(c.id); if (e.key === 'Escape') setEditing(null); }} autoFocus />
                      <button onClick={() => saveEdit(c.id)} className="p-1 bg-green-500 text-white rounded text-xs"><Check size={11} /></button>
                      <button onClick={() => setEditing(null)} className="p-1 bg-gray-200 text-gray-600 rounded text-xs"><X size={11} /></button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.comment}</p>
                  )}
                </div>
                {c.user_id === user?.id && editing !== c.id && (
                  <div className="flex gap-2 mt-0.5 ml-2">
                    <button onClick={() => { setEditing(c.id); setEditText(c.comment); }} className="text-xs text-gray-400 hover:text-[#1e3a5f]">Edit</button>
                    <button onClick={() => del(c.id)} className="text-xs text-gray-400 hover:text-red-500">Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex gap-2 px-4 py-3 border-t bg-gray-50 rounded-b-2xl">
          <input value={text} onChange={e => setText(e.target.value)} className="input-field flex-1 text-sm"
            placeholder="Write a comment…" onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); }}} />
          <button type="submit" disabled={sending || !text.trim()} className="btn-primary px-3 py-2 flex items-center gap-1 text-sm disabled:opacity-40">
            <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Task row (list view) ───────────────────────────────────────────────────────
function TaskRow({ task, onEdit, onStatusChange, onTimelog, onComment, onDelete, staff }) {
  const today = new Date().toISOString().split('T')[0];
  const isOverdue = task.due_date && task.due_date < today && !['Done', 'Cancelled'].includes(task.status);

  return (
    <div className={`group flex items-start gap-3 p-3 rounded-xl border transition-all hover:shadow-sm cursor-pointer
      ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-gray-100 hover:border-[#f0a500] bg-white'}`}
      onClick={() => onEdit(task)}>
      {/* Priority dot */}
      <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${task.priority === 'High' ? 'bg-red-500' : task.priority === 'Medium' ? 'bg-amber-500' : 'bg-green-500'}`} title={task.priority} />

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <span className={`text-sm font-medium ${task.status === 'Done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{task.title}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLOR[task.task_type] || TYPE_COLOR.general}`}>{task.task_type}</span>
          {task.recurring && task.recurring !== 'none' && <Repeat size={11} className="text-purple-400 mt-0.5" title={`Recurring: ${task.recurring}`} />}
          {task.review_required ? <Eye size={11} className="text-amber-400 mt-0.5" title="Review required" /> : null}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {task.client_name && <span className="text-xs text-gray-500">{task.client_name}</span>}
          {task.assignee_name && <span className="text-xs text-gray-400">→ {task.assignee_name}</span>}
          {task.due_date && (
            <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
              {isOverdue ? '⚠ ' : ''}{fmtDate(task.due_date)}
            </span>
          )}
          {task.estimated_hours ? <span className="text-xs text-gray-400">{task.estimated_hours}h est</span> : null}
          {task.actual_hours > 0 ? <span className="text-xs text-blue-500">{task.actual_hours?.toFixed(1)}h logged</span> : null}
          {task.subtask_count > 0 && (
            <span className="text-xs text-gray-400">{task.subtask_done}/{task.subtask_count} subtasks</span>
          )}
        </div>
      </div>

      {/* Status + actions */}
      <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <select value={task.status} onChange={e => onStatusChange(task, e.target.value)}
          className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer ${STATUS_COLOR[task.status] || ''}`}>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <div className="hidden group-hover:flex items-center gap-1">
          <button onClick={() => onTimelog(task)} className="p-1 text-gray-400 hover:text-blue-500 rounded" title="Log time"><Timer size={13} /></button>
          <button onClick={() => onComment(task)} className="p-1 text-gray-400 hover:text-[#1e3a5f] rounded" title="Comments">
            <MessageSquare size={13} />
            {task.comment_count > 0 && <span className="ml-0.5 text-xs text-[#1e3a5f]">{task.comment_count}</span>}
          </button>
          <button onClick={() => onDelete(task)} className="p-1 text-gray-300 hover:text-red-500 rounded" title="Delete"><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

// ── Kanban card ────────────────────────────────────────────────────────────────
const MODULE_BADGE = { itr: 'bg-purple-100 text-purple-700', tds: 'bg-blue-100 text-blue-700', gst: 'bg-orange-100 text-orange-700', audit: 'bg-red-100 text-red-700', roc: 'bg-indigo-100 text-indigo-700', esipf: 'bg-teal-100 text-teal-700', accounts: 'bg-gray-100 text-gray-600' };
const PRI_LEFT = { High: 'border-l-red-500', Medium: 'border-l-amber-400', Low: 'border-l-green-400' };

function KanbanCard({ task, onEdit, onStatusChange, onTimelog, onComment, dragging, onDragStart, onDragEnd }) {
  const today = new Date().toISOString().split('T')[0];
  const isOverdue = task.due_date && task.due_date < today && !['Done', 'Cancelled'].includes(task.status);
  const isDueToday = task.due_date === today && !['Done', 'Cancelled'].includes(task.status);
  const initials = task.assignee_name ? task.assignee_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : null;
  const modKey = task.compliance_module?.toLowerCase();

  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('text/plain', String(task.id)); onDragStart?.(task.id); }}
      onDragEnd={onDragEnd}
      onClick={() => onEdit(task)}
      style={{ opacity: dragging ? 0.45 : 1 }}
      className={`bg-white rounded-lg border border-l-4 cursor-pointer transition-all hover:shadow-md
        ${isOverdue ? 'border-red-200 border-l-red-500' : PRI_LEFT[task.priority] || 'border-l-gray-300'}
        ${isOverdue ? '' : 'hover:border-[#f0a500]'} select-none`}>
      {/* Top row: module badge + priority */}
      <div className="flex items-center justify-between px-2.5 pt-2 pb-0 gap-1">
        <div className="flex items-center gap-1 flex-wrap">
          {modKey && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${MODULE_BADGE[modKey] || 'bg-gray-100 text-gray-500'}`}>{task.compliance_module}</span>}
          {task.task_type && task.task_type !== 'compliance' && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${TYPE_COLOR[task.task_type] || ''}`}>{task.task_type}</span>
          )}
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${PRIORITY_COLOR[task.priority] || ''}`}>{task.priority}</span>
      </div>
      {/* Title */}
      <div className="px-2.5 pt-1.5 pb-1">
        <p className={`text-[13px] font-medium leading-snug line-clamp-2 ${task.status === 'Done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{task.title}</p>
      </div>
      {/* Client */}
      {task.client_name && (
        <div className="px-2.5 pb-1 flex items-center gap-1.5">
          <span className="text-[11px] text-gray-500 truncate">{task.client_name}</span>
          {task.client_code && <span className="text-[10px] text-gray-300 flex-shrink-0">{task.client_code}</span>}
        </div>
      )}
      {/* Footer */}
      <div className="flex items-center justify-between px-2.5 pb-2 pt-0.5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          {task.due_date && (
            <span className={`text-[11px] font-medium ${isOverdue ? 'text-red-600' : isDueToday ? 'text-amber-600' : 'text-gray-400'}`}>
              {isOverdue ? '⚠ ' : isDueToday ? '📅 ' : ''}{fmtDate(task.due_date)}
            </span>
          )}
          {task.subtask_count > 0 && (
            <span className="text-[10px] text-gray-400">{task.subtask_done}/{task.subtask_count}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {task.comment_count > 0 && (
            <button onClick={() => onComment(task)} className="flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-[#1e3a5f]">
              <MessageSquare size={10} />{task.comment_count}
            </button>
          )}
          <button onClick={() => onTimelog(task)} className="text-gray-300 hover:text-blue-500"><Timer size={10} /></button>
          {initials && (
            <div className="w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0" title={task.assignee_name}>
              {initials}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Template card ──────────────────────────────────────────────────────────────
function TemplateCard({ tmpl, staff, clients, onCreated }) {
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ client_id: '', assigned_to: '', due_date: '' });
  const [creating, setCreating] = useState(false);

  const create = async e => {
    e.stopPropagation();
    setCreating(true);
    try {
      const r = await api.post('/tasks/from-template', { template_id: tmpl.id, ...form, client_id: form.client_id || null, assigned_to: form.assigned_to || null });
      toast.success(`Created ${r.data.count} tasks from "${tmpl.name}"`);
      setShowForm(false); onCreated();
    } catch { toast.error('Failed'); }
    finally { setCreating(false); }
  };

  const CATEGORY_COLOR = { onboarding: 'bg-blue-100 text-blue-700', 'itr-season': 'bg-purple-100 text-purple-700', 'gst-setup': 'bg-orange-100 text-orange-700', audit: 'bg-red-100 text-red-700', incorporation: 'bg-indigo-100 text-indigo-700', custom: 'bg-gray-100 text-gray-600' };

  return (
    <div className="card hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">{tmpl.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{tmpl.description}</p>
        </div>
        <div className="flex items-center gap-2 ml-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLOR[tmpl.category] || CATEGORY_COLOR.custom}`}>{tmpl.category}</span>
          {tmpl.is_system ? <Star size={12} className="text-amber-400" title="System template" /> : null}
        </div>
      </div>
      <div className="text-xs text-gray-500 mb-3">{tmpl.items?.length || 0} tasks · ~{(tmpl.items || []).reduce((s, i) => s + (i.estimated_hours || 0), 0).toFixed(1)}h total</div>

      <button onClick={() => setExpanded(e => !e)} className="text-xs text-[#1e3a5f] hover:text-[#f0a500] mb-3 flex items-center gap-1">
        <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} /> {expanded ? 'Hide' : 'View'} tasks
      </button>

      {expanded && (
        <div className="mb-3 space-y-1 bg-gray-50 rounded-lg p-2">
          {(tmpl.items || []).map(item => (
            <div key={item.id} className="flex items-center gap-2 text-xs py-0.5">
              <Circle size={8} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-700 flex-1">{item.title}</span>
              <span className="text-gray-400">{item.estimated_hours}h</span>
              <span className={`px-1 rounded text-gray-500 bg-gray-200`}>{item.assigned_role}</span>
            </div>
          ))}
        </div>
      )}

      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="w-full btn-primary text-xs py-2 flex items-center justify-center gap-1">
          <Plus size={12} /> Use Template
        </button>
      ) : (
        <div className="space-y-2 mt-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
          <ClientSearchDropdown value={form.client_id} initialLabel="" onChange={id => setForm(p => ({ ...p, client_id: id }))} placeholder="Select client (optional)…" />
          <select value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} className="input-field text-xs">
            <option value="">Assign to…</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} className="input-field text-xs" />
          <div className="flex gap-2">
            <button onClick={create} disabled={creating} className="flex-1 btn-primary text-xs py-1.5">{creating ? 'Creating…' : 'Create Tasks'}</button>
            <button onClick={() => setShowForm(false)} className="flex-1 btn-secondary text-xs py-1.5">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [stats, setStats] = useState({});
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [timelogTask, setTimelogTask] = useState(null);
  const [commentTask, setCommentTask] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [quickAdd, setQuickAdd] = useState('');
  const [weekExpanded, setWeekExpanded] = useState(false);
  // Bulk select + archive (All Tasks tab)
  const [selected, setSelected] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);

  const loadTasks = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (priorityFilter) params.priority = priorityFilter;
    if (typeFilter) params.task_type = typeFilter;
    if (assigneeFilter) params.assigned_to = assigneeFilter;
    if (search) params.search = search;
    if (showArchived) params.show_archived = '1';
    api.get('/tasks', { params }).then(r => { setTasks(r.data); setSelected(new Set()); });
  }, [statusFilter, priorityFilter, typeFilter, assigneeFilter, search, showArchived]);

  const loadAll = useCallback(() => {
    loadTasks();
    api.get('/tasks/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/tasks/templates').then(r => setTemplates(r.data)).catch(() => {});
  }, [loadTasks]);

  useEffect(() => {
    api.get('/auth/users').then(r => setStaff(r.data));
    loadAll();
  }, [loadAll]);

  const openEdit = task => { setEditing(task); setShowModal(true); };
  const openNew  = () => { setEditing(null); setShowModal(true); };

  // ── Bulk actions ────────────────────────────────────────────────────────────
  const toggleSel = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkArchive = async archive => {
    try { await api.post('/tasks/bulk-archive', { ids: [...selected], archive }); toast.success(archive ? 'Archived' : 'Unarchived'); setSelected(new Set()); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };
  const bulkDelete = async () => {
    try { await api.post('/tasks/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); setSelected(new Set()); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };
  const bulkUpdate = async updates => {
    try { await api.post('/tasks/bulk-update', { ids: [...selected], updates }); toast.success('Updated'); setSelected(new Set()); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  // Alt+I — open the New Task form
  useEffect(() => {
    const h = () => { setEditing(null); setShowModal(true); };
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task.id}`, { ...task, status: newStatus });
      loadTasks();
      api.get('/tasks/stats').then(r => setStats(r.data));
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async task => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try { await api.delete(`/tasks/${task.id}`); loadTasks(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const handleDrop = async (colStatus, e) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    const task = tasks.find(t => String(t.id) === taskId);
    if (!task || task.status === colStatus) { setDraggingId(null); setDragOverCol(null); return; }
    try {
      await api.put(`/tasks/${taskId}`, { ...task, status: colStatus });
      loadTasks();
      api.get('/tasks/stats').then(r => setStats(r.data));
    } catch { toast.error('Failed to move task'); }
    setDraggingId(null); setDragOverCol(null);
  };

  const handleQuickAdd = async e => {
    e.preventDefault();
    if (!quickAdd.trim()) return;
    try {
      await api.post('/tasks', { title: quickAdd.trim(), assigned_to: user?.id, due_date: today, priority: 'Medium', status: 'To Do', task_type: 'general' });
      setQuickAdd('');
      loadAll();
      toast.success('Task added');
    } catch { toast.error('Failed'); }
  };

  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  const myTasks = tasks.filter(t => String(t.assigned_to) === String(user?.id) && !['Done', 'Cancelled'].includes(t.status));
  const overdueTasks = tasks.filter(t => t.due_date && t.due_date < today && !['Done', 'Cancelled'].includes(t.status));

  const filteredTasks = tasks.filter(t => !search ||
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.client_name?.toLowerCase().includes(search.toLowerCase()));

  const myOverdue  = myTasks.filter(t => t.due_date && t.due_date < today);
  const myDueToday = myTasks.filter(t => t.due_date === today);
  const myInProgress = myTasks.filter(t => t.status === 'In Progress');
  const myThisWeek = myTasks.filter(t => t.due_date && t.due_date > today && t.due_date <= weekEnd && t.status !== 'In Progress');
  const myDone = tasks.filter(t => String(t.assigned_to) === String(user?.id) && t.status === 'Done' && t.completed_at?.slice(0, 10) === today);
  const myEstHoursLeft = myTasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const TABS = [
    { key: 'myday', label: 'My Day', count: myOverdue.length + myDueToday.length || undefined },
    { key: 'all', label: 'All Tasks', count: tasks.length },
    { key: 'mine', label: 'My Tasks', count: myTasks.length },
    { key: 'kanban', label: 'Kanban' },
    { key: 'templates', label: 'Templates', count: templates.length },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Task Manager</h1>
          <p className="text-xs text-gray-500 mt-0.5">{stats.mine || 0} assigned to you · {stats.overdue || 0} overdue</p>
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2"><Plus size={16} /> New Task</button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total Tasks" value={stats.total || 0} icon={CheckSquare} />
        <StatCard label="My Open Tasks" value={stats.mine || 0} icon={Users} color="text-blue-600" />
        <StatCard label="Overdue" value={stats.overdue || 0} icon={AlertTriangle} color={stats.overdue > 0 ? "text-red-600" : "text-gray-400"} />
        <StatCard label="Review Pending" value={stats.review_pending || 0} icon={Eye} color="text-amber-600" />
        <StatCard label="Done This Month" value={stats.done_month || 0} icon={Check} color="text-green-600" />
      </div>

      {/* Overdue alert */}
      {overdueTasks.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} />
          <strong>{overdueTasks.length}</strong> overdue {overdueTasks.length === 1 ? 'task' : 'tasks'} — {overdueTasks.map(t => t.title).slice(0, 3).join(', ')}{overdueTasks.length > 3 ? '…' : ''}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}{t.count !== undefined ? <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 rounded-full px-1.5">{t.count}</span> : null}
          </button>
        ))}
      </div>

      {/* ── MY DAY ── */}
      {tab === 'myday' && (
        <div className="space-y-4">
          {/* Greeting */}
          <div className="card bg-gradient-to-r from-[#1e3a5f] to-[#2a5298] text-white py-4">
            <h2 className="text-lg font-bold">{greeting}, {user?.name?.split(' ')[0]}! 👋</h2>
            <p className="text-blue-100 text-sm mt-0.5">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm text-blue-100">
              <span>{myTasks.length} pending</span>
              {myOverdue.length > 0 && <span className="text-red-300 font-medium">⚠ {myOverdue.length} overdue</span>}
              {myEstHoursLeft > 0 && <span>~{myEstHoursLeft.toFixed(1)}h remaining</span>}
            </div>
          </div>

          {/* Quick add */}
          <form onSubmit={handleQuickAdd} className="flex gap-2">
            <input value={quickAdd} onChange={e => setQuickAdd(e.target.value)}
              placeholder="What do you need to do today? Press Enter to add…"
              className="input-field flex-1 text-sm" />
            <button type="submit" className="btn-primary px-4 text-sm">Add</button>
          </form>

          {/* Overdue */}
          {myOverdue.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-red-500" />
                <span className="text-sm font-bold text-red-600">Overdue ({myOverdue.length})</span>
              </div>
              <div className="space-y-1.5">
                {myOverdue.map(task => {
                  const days = Math.floor((new Date(today) - new Date(task.due_date)) / 86400000);
                  return (
                    <div key={task.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(task)}>
                        <div className="text-sm font-medium text-gray-800 truncate">{task.title}</div>
                        <div className="text-xs text-red-600 mt-0.5">{days} day{days !== 1 ? 's' : ''} overdue · {task.client_name || 'No client'}</div>
                      </div>
                      <button onClick={() => handleStatusChange(task, 'Done')} className="flex-shrink-0 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium">✓ Done</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Due Today */}
          {myDueToday.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">📅</span>
                <span className="text-sm font-bold text-amber-600">Due Today ({myDueToday.length})</span>
              </div>
              <div className="space-y-1.5">
                {myDueToday.sort((a, b) => (b.priority === 'High') - (a.priority === 'High')).map(task => (
                  <div key={task.id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(task)}>
                      <div className="text-sm font-medium text-gray-800 truncate">{task.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLOR[task.priority]}`}>{task.priority}</span>
                        {task.client_name && <span className="text-xs text-gray-500">{task.client_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {task.status !== 'In Progress' && (
                        <button onClick={() => handleStatusChange(task, 'In Progress')} className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded-lg font-medium">▶ Start</button>
                      )}
                      <button onClick={() => handleStatusChange(task, 'Done')} className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2 py-1 rounded-lg font-medium">✓ Done</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* In Progress */}
          {myInProgress.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">🔄</span>
                <span className="text-sm font-bold text-blue-600">In Progress ({myInProgress.length})</span>
              </div>
              <div className="space-y-1.5">
                {myInProgress.map(task => (
                  <div key={task.id} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(task)}>
                      <div className="text-sm font-medium text-gray-800 truncate">{task.title}</div>
                      {task.client_name && <div className="text-xs text-gray-500 mt-0.5">{task.client_name}</div>}
                    </div>
                    <button onClick={() => handleStatusChange(task, 'Done')} className="flex-shrink-0 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium">✓ Done</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* This Week (collapsed) */}
          {myThisWeek.length > 0 && (
            <div>
              <button onClick={() => setWeekExpanded(e => !e)} className="flex items-center gap-2 mb-2 text-left w-full">
                <ChevronRight size={14} className={`text-gray-500 transition-transform ${weekExpanded ? 'rotate-90' : ''}`} />
                <span className="text-sm font-bold text-gray-600">📋 This Week ({myThisWeek.length})</span>
              </button>
              {weekExpanded && (
                <div className="space-y-1.5">
                  {myThisWeek.map(task => (
                    <div key={task.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100" onClick={() => openEdit(task)}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-700 truncate">{task.title}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {task.due_date && <span className="text-xs text-gray-400">{fmtDate(task.due_date)}</span>}
                          {task.client_name && <span className="text-xs text-gray-400">{task.client_name}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {myTasks.length === 0 && (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">🎉</div>
              <div className="text-gray-500 font-medium">You're all caught up!</div>
              <div className="text-gray-400 text-sm mt-1">No tasks assigned to you right now.</div>
              <button onClick={openNew} className="mt-4 btn-primary text-sm">+ Create a task</button>
            </div>
          )}

          {/* Daily progress footer */}
          {(myTasks.length > 0 || myDone.length > 0) && (
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Today's progress</span>
                <span className="text-sm text-gray-500">{myDone.length}/{myDone.length + myTasks.length} done</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${myDone.length + myTasks.length > 0 ? Math.round(myDone.length / (myDone.length + myTasks.length) * 100) : 0}%` }} />
              </div>
              <div className="text-xs text-gray-400 mt-1.5">
                {myDone.length / (myDone.length + myTasks.length) >= 0.5 ? '🌟 Great work!' : '💪 Keep going!'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ALL TASKS ── */}
      {tab === 'all' && (
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks or clients…"
                className="input-field pl-8 text-sm" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-36 text-sm">
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="input-field w-28 text-sm">
              <option value="">All Priority</option>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="input-field w-32 text-sm capitalize">
              <option value="">All Types</option>
              {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} className="input-field w-36 text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={() => setShowArchived(a => !a)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>
              {showArchived ? '📂 Archived' : '📁 Show Archived'}
            </button>
          </div>

          <BulkActionBar module="tasks" count={selected.size} staff={staff} statuses={STATUSES}
            onAssign={id => bulkUpdate({ assigned_to: id })}
            onStatus={s => bulkUpdate({ status: s })}
            onArchive={archive => bulkArchive(archive)}
            archivedView={showArchived}
            onDelete={bulkDelete}
            onClear={() => setSelected(new Set())} />

          {filteredTasks.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-gray-500 pl-1 cursor-pointer">
              <input type="checkbox"
                checked={filteredTasks.length > 0 && selected.size === filteredTasks.length}
                onChange={e => setSelected(e.target.checked ? new Set(filteredTasks.map(t => t.id)) : new Set())}
                className="rounded" />
              Select all ({filteredTasks.length})
            </label>
          )}
          {filteredTasks.length === 0 && (
            <div className="text-center text-gray-400 py-12 text-sm">
              {showArchived ? 'No archived tasks.' : <>No tasks found. <button onClick={openNew} className="text-[#1e3a5f] font-medium hover:underline">Create one</button></>}
            </div>
          )}
          <div className="space-y-1.5">
            {filteredTasks.map(task => (
              <div key={task.id} className="flex items-start gap-2">
                <input type="checkbox" checked={selected.has(task.id)} onChange={() => toggleSel(task.id)}
                  className="rounded mt-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <TaskRow task={task} staff={staff}
                    onEdit={openEdit} onStatusChange={handleStatusChange}
                    onTimelog={setTimelogTask} onComment={setCommentTask} onDelete={handleDelete} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MY TASKS ── */}
      {tab === 'mine' && (
        <div className="space-y-3">
          {myTasks.length === 0 && (
            <div className="text-center text-gray-400 py-12 text-sm">No tasks assigned to you right now. 🎉</div>
          )}
          {/* Group by priority */}
          {['High', 'Medium', 'Low'].map(pri => {
            const group = myTasks.filter(t => t.priority === pri);
            if (!group.length) return null;
            return (
              <div key={pri}>
                <div className={`text-xs font-bold uppercase tracking-wide mb-1.5 ${pri === 'High' ? 'text-red-600' : pri === 'Medium' ? 'text-amber-600' : 'text-green-600'}`}>
                  {pri} Priority — {group.length}
                </div>
                <div className="space-y-1.5">
                  {group.map(task => (
                    <TaskRow key={task.id} task={task} staff={staff}
                      onEdit={openEdit} onStatusChange={handleStatusChange}
                      onTimelog={setTimelogTask} onComment={setCommentTask} onDelete={handleDelete} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── KANBAN ── */}
      {tab === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '70vh' }}>
          {KANBAN_COLS.map(col => {
            const colTasks = tasks.filter(t => t.status === col.status);
            const isOver = dragOverCol === col.status;
            return (
              <div key={col.status}
                className={`flex-shrink-0 w-64 rounded-xl border-t-4 ${col.color} bg-white shadow-sm flex flex-col transition-all ${isOver ? 'ring-2 ring-[#f0a500] ring-offset-1' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOverCol(col.status); }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={e => handleDrop(col.status, e)}>
                {/* Column header */}
                <div className={`px-3 py-2.5 ${col.bg} flex items-center justify-between rounded-t-xl flex-shrink-0`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                    <span className="text-sm font-semibold text-gray-700">{col.label}</span>
                  </div>
                  <span className="text-xs bg-white rounded-full px-2 py-0.5 text-gray-600 font-medium shadow-sm">{colTasks.length}</span>
                </div>
                {/* Cards */}
                <div className="p-2 space-y-2 overflow-y-auto flex-1" style={{ maxHeight: 'calc(70vh - 80px)' }}>
                  {colTasks.map(task => (
                    <KanbanCard key={task.id} task={task}
                      dragging={draggingId === task.id}
                      onDragStart={id => setDraggingId(id)}
                      onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                      onEdit={openEdit} onStatusChange={handleStatusChange}
                      onTimelog={setTimelogTask} onComment={setCommentTask} />
                  ))}
                  {colTasks.length === 0 && !isOver && (
                    <div className="text-center text-gray-300 text-xs py-6 border-2 border-dashed border-gray-100 rounded-lg">Drop here</div>
                  )}
                  {isOver && (
                    <div className="text-center text-[#f0a500] text-xs py-6 border-2 border-dashed border-[#f0a500] rounded-lg bg-amber-50/50">Move here</div>
                  )}
                </div>
                {/* Add task footer */}
                <div className="p-2 border-t flex-shrink-0">
                  <button onClick={() => { setEditing({ status: col.status }); setShowModal(true); }}
                    className="w-full flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-[#1e3a5f] py-1.5 hover:bg-gray-50 rounded-lg transition-colors">
                    <Plus size={12} /> Add task
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── TEMPLATES ── */}
      {tab === 'templates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{templates.length} templates · Click "Use Template" to create tasks in bulk</p>
          </div>
          {templates.length === 0 && (
            <div className="text-center text-gray-400 py-12 text-sm">No templates found. System templates will appear after server restart.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(tmpl => (
              <TemplateCard key={tmpl.id} tmpl={tmpl} staff={staff} onCreated={loadAll} />
            ))}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showModal && (
        <TaskModal editing={editing} staff={staff} onClose={() => { setShowModal(false); setEditing(null); }}
          onSave={() => { setShowModal(false); setEditing(null); loadAll(); }} />
      )}
      {timelogTask && (
        <TimeLogModal taskId={timelogTask.id} taskTitle={timelogTask.title}
          onClose={() => setTimelogTask(null)} onSaved={loadTasks} />
      )}
      {commentTask && (
        <CommentsPanel taskId={commentTask.id} taskTitle={commentTask.title}
          onClose={() => setCommentTask(null)} />
      )}
    </div>
  );
}
